<template>
  <div class="login-container">
    <el-form
      ref="loginForm"
      :model="loginForm"
      label-position="left"
      class="login-form"
      autocomplete="on"
    >
      <div class="title-container">
        <h3 class="title">登录</h3>
      </div>

      <el-form-item prop="username">
        <el-input
          ref="usernameInput"
          v-model="loginForm.username"
          placeholder="用户名"
          name="username"
          type="text"
          tabindex="1"
          autocomplete="on"
          @keyup.enter.native="handleLogin"
        />
      </el-form-item>

      <el-tooltip v-model="capsTooltip" content="Caps lock is On" placement="right" manual>
        <el-form-item prop="password">
          <el-input
            ref="passwordInput"
            v-model="loginForm.password"
            type="password"
            placeholder="密码"
            name="password"
            tabindex="2"
            autocomplete="on"
            @keyup.native="checkCapslock"
            @blur="capsTooltip = false"
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>
      </el-tooltip>

      <div
        v-if="aliyunCaptchaEnabled"
        id="aliyun-captcha-element-admin"
        style="position: fixed; left: -9999px; width: 1px; height: 1px; overflow: hidden"
      />
      <button
        v-if="aliyunCaptchaEnabled"
        id="aliyun-captcha-button-admin"
        type="button"
        style="position: fixed; left: -9999px; opacity: 0"
        tabindex="-1"
        aria-hidden="true"
      />

      <el-form-item v-if="!aliyunCaptchaEnabled" prop="captchaCode" class="captcha-code">
        <el-input
          ref="captchaInput"
          v-model="loginForm.captchaCode"
          placeholder="验证码"
          name="captchaCode"
          type="text"
          tabindex="3"
          autocomplete="off"
          @keyup.enter.native="handleLogin"
        />
        <div v-if="loginForm.captchaUrl" class="captcha-code-img">
          <a @click="showCaptcha"><img :src="loginForm.captchaUrl" /></a>
        </div>
      </el-form-item>
      <el-button
        :loading="loading"
        type="primary"
        style="width: 100%; margin-bottom: 30px"
        @click.native.prevent="handleLogin"
      >
        Login
      </el-button>
    </el-form>
  </div>
</template>

<script>
import {
  prepareAliyunCaptcha,
  bindAliyunCaptchaWidgetWhenReady,
  ensureAliyunCaptchaVerifyDelay,
} from "@/utils/aliyunCaptcha";

export default {
  data() {
    return {
      loginForm: {
        username: "",
        password: "",
        captchaId: "",
        captchaUrl: "",
        captchaCode: "",
      },
      publicConfig: {},
      capsTooltip: false,
      loading: false,
      redirect: undefined,
      otherQuery: {},
      aliyunCaptchaWidgetInited: false,
      aliyunCaptchaBooting: false,
      cancelAliyunBind: null,
      aliyunCaptchaInstance: null,
    };
  },
  computed: {
    aliyunCaptchaEnabled() {
      return !!(this.publicConfig && this.publicConfig.aliyunCaptchaEnabled);
    },
  },
  watch: {
    aliyunCaptchaEnabled() {
      this.$nextTick(() => this.bootstrapCaptcha());
    },
    $route: {
      handler(route) {
        const { query } = route;
        if (query) {
          this.redirect = query.redirect;
          this.otherQuery = this.getOtherQuery(query);
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    if (this.cancelAliyunBind) {
      this.cancelAliyunBind();
      this.cancelAliyunBind = null;
    }
    this.destroyAliyunCaptchaInstance();
  },
  async mounted() {
    await this.loadPublicConfig();
    this.$nextTick(() => this.bootstrapCaptcha());
    if (this.loginForm.username === "") {
      this.$refs.usernameInput.focus();
    } else if (this.loginForm.password === "") {
      this.$refs.passwordInput.focus();
    }
  },
  methods: {
    async loadPublicConfig() {
      try {
        const cfg = await this.axios.get("/api/config/configs");
        this.publicConfig = cfg || {};
      } catch (e) {
        this.publicConfig = {};
      }
    },
    bootstrapCaptcha() {
      if (!this.aliyunCaptchaEnabled) {
        this.showCaptcha();
      }
      this.initAliyunCaptchaIfNeeded();
    },
    initAliyunCaptchaIfNeeded() {
      if (!this.aliyunCaptchaEnabled || typeof window === "undefined") {
        return;
      }
      if (this.aliyunCaptchaWidgetInited || this.aliyunCaptchaBooting) {
        return;
      }
      const prefix = this.publicConfig.aliyunCaptchaPrefix;
      const sceneId = this.publicConfig.aliyunCaptchaSceneId;
      const region = this.publicConfig.aliyunCaptchaRegion || "cn";
      if (!prefix || !sceneId) {
        return;
      }
      this.aliyunCaptchaBooting = true;
      prepareAliyunCaptcha(region, prefix)
        .then(() => {
          if (this.aliyunCaptchaWidgetInited) {
            this.aliyunCaptchaBooting = false;
            return;
          }
          if (this.cancelAliyunBind) {
            this.cancelAliyunBind();
          }
          this.cancelAliyunBind = bindAliyunCaptchaWidgetWhenReady({
            sceneId,
            elementSelector: "#aliyun-captcha-element-admin",
            buttonSelector: "#aliyun-captcha-button-admin",
            shouldKeepTrying: () => !!this.aliyunCaptchaEnabled,
            onAbort: () => {
              this.aliyunCaptchaBooting = false;
            },
            onBound: () => {
              this.aliyunCaptchaWidgetInited = true;
              this.aliyunCaptchaBooting = false;
              this.cancelAliyunBind = null;
            },
            onGiveUp: () => {
              this.aliyunCaptchaBooting = false;
              if (this.aliyunCaptchaEnabled) {
                this.$message.error("智能验证初始化失败，请刷新页面重试");
              }
            },
            onSuccess: (param) => this.afterAliyunCaptcha(param),
            onFail: (info) => {
              this.$message.error((info && (info.msg || info.message)) || "智能验证未通过");
            },
            getInstance: (instance) => {
              this.aliyunCaptchaInstance = instance;
            },
          });
        })
        .catch((e) => {
          this.aliyunCaptchaBooting = false;
          this.$message.error(e.message || "验证码脚本加载失败");
        });
    },
    destroyAliyunCaptchaInstance() {
      const inst = this.aliyunCaptchaInstance;
      if (inst && typeof inst.destroyCaptcha === "function") {
        try {
          inst.destroyCaptcha();
        } catch (e) {
          /* ignore */
        }
      }
      this.aliyunCaptchaInstance = null;
    },
    resetAliyunCaptchaAfterAuthFailure() {
      const inst = this.aliyunCaptchaInstance;
      if (inst && typeof inst.refresh === "function") {
        try {
          inst.refresh();
          return;
        } catch (e) {
          /* fall through */
        }
      }
      this.destroyAliyunCaptchaInstance();
      this.aliyunCaptchaWidgetInited = false;
      this.aliyunCaptchaBooting = false;
      if (this.cancelAliyunBind) {
        this.cancelAliyunBind();
        this.cancelAliyunBind = null;
      }
      this.$nextTick(() => this.initAliyunCaptchaIfNeeded());
    },
    async afterAliyunCaptcha(captchaVerifyParam) {
      await this.doLogin(captchaVerifyParam);
    },
    async showCaptcha() {
      if (this.aliyunCaptchaEnabled) {
        return;
      }
      try {
        const ret = await this.axios.get("/api/captcha/request", {
          params: {
            captchaId: this.loginForm.captchaId || "",
          },
        });
        this.loginForm.captchaId = ret.captchaId;
        this.loginForm.captchaUrl =
          process.env.VUE_APP_BASE_API +
          "/api/captcha/show?captchaId=" +
          this.loginForm.captchaId +
          "&timestamp=" +
          new Date().getTime();
      } catch (e) {
        this.$message.error(e.message || e);
      }
    },
    checkCapslock({ shiftKey, key } = {}) {
      if (key && key.length === 1) {
        if ((shiftKey && key >= "a" && key <= "z") || (!shiftKey && key >= "A" && key <= "Z")) {
          this.capsTooltip = true;
        } else {
          this.capsTooltip = false;
        }
      }
      if (key === "CapsLock" && this.capsTooltip === true) {
        this.capsTooltip = false;
      }
    },
    handleLogin() {
      const u = (this.loginForm.username || "").trim();
      if (!u) {
        this.$message.error("请输入用户名");
        return;
      }
      if (!this.loginForm.password) {
        this.$message.error("请输入密码");
        return;
      }
      if (this.aliyunCaptchaEnabled) {
        const btn =
          typeof document !== "undefined" && document.getElementById("aliyun-captcha-button-admin");
        if (!btn || typeof window.initAliyunCaptcha !== "function") {
          this.$message.error("智能验证未就绪，请刷新页面重试");
          return;
        }
        ensureAliyunCaptchaVerifyDelay().then(() => {
          btn.click();
        });
        return;
      }
      if (!this.loginForm.captchaCode) {
        this.$message.error("请输入验证码");
        return;
      }
      this.doLogin("");
    },
    async doLogin(captchaVerifyParam) {
      this.loading = true;
      try {
        await this.$store.dispatch("user/login", {
          ...this.loginForm,
          captchaVerifyParam: captchaVerifyParam || "",
        });
        this.$router.push({ path: this.redirect || "/", query: this.otherQuery });
      } catch (e) {
        if (this.aliyunCaptchaEnabled) {
          this.resetAliyunCaptchaAfterAuthFailure();
        } else {
          this.showCaptcha();
        }
      } finally {
        this.loading = false;
      }
    },
    getOtherQuery(query) {
      return Object.keys(query).reduce((acc, cur) => {
        if (cur !== "redirect") {
          acc[cur] = query[cur];
        }
        return acc;
      }, {});
    },
  },
};
</script>

<style lang="scss">
.login-container {
  min-height: 100%;
  width: 100%;
  background-color: #fff;
  overflow: hidden;

  .login-form {
    position: relative;
    width: 520px;
    max-width: 100%;
    padding: 160px 35px 0;
    margin: 0 auto;
    overflow: hidden;

    .captcha-code {
      & > div {
        display: flex;
        .captcha-code-img {
          img {
            height: 36px;
          }
        }
      }
    }
  }

  .tips {
    font-size: 14px;
    color: #fff;
    margin-bottom: 10px;

    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }

  .title-container {
    position: relative;

    .title {
      font-size: 26px;
      color: #000;
      margin: 0 auto 40px auto;
      text-align: center;
      font-weight: bold;
    }
  }
}
</style>
